// Search mode panel for UnifiedCourseModal

import { Search, Loader2 } from 'lucide-react';
import GradeDistributionChart from '../../GradeDistributionChart';
import { useGradeOptions } from '@/hooks/useGradeOptions';
import { useInstitutionConfig, useInstitutionTheme } from '../../../../contexts/InstitutionConfigContext';

export interface SearchableCourse {
  id?: string | number;
  code: string;
  title?: string;
  credits?: number;
  grade_data?: {
    distribution?: Record<string, number>;
    total_students?: number;
    average_gpa?: number;
  } | null;
  [key: string]: unknown;
}

export interface PlaceholderFormData {
  grade?: string;
  [key: string]: unknown;
}

export type CourseModalContext = 'semester' | 'bookmark' | 'other_credits';

export type CourseModalMode = 'search' | 'placeholder';

export interface SearchPanelProps {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  searchLoading: boolean;
  sortBy: string;
  setSortBy: (value: string) => void;
  sortedResults: SearchableCourse[];
  selectedCourse: SearchableCourse | null;
  setSelectedCourse: (course: SearchableCourse | null) => void;
  isCourseInPlan: (code: string) => boolean;
  isCourseBookmarked: (code: string) => boolean;
  context: CourseModalContext;
  placeholderData: PlaceholderFormData;
  handleInputChange: (field: string, value: unknown) => void;
  selectedBgClass: string;
  accentColor: string;
  setMode: (mode: CourseModalMode) => void;
  errors: { course?: string; [key: string]: string | undefined };
}

const SearchPanel = ({
  searchQuery,
  setSearchQuery,
  searchLoading,
  sortBy,
  setSortBy,
  sortedResults,
  selectedCourse,
  setSelectedCourse,
  isCourseInPlan,
  isCourseBookmarked,
  context,
  placeholderData,
  handleInputChange,
  selectedBgClass,
  accentColor,
  setMode,
  errors,
}: SearchPanelProps) => {
  const gradeOptions = useGradeOptions();
  // Gate the grade distribution chart on the institution flag (see
  // InstitutionConfigContext). Existing optional chaining stays as the
  // safety net for missing per-course data.
  const { config: institutionConfig } = useInstitutionConfig();
  const showGradeUI = institutionConfig.hasGradeData;
  // Phase 6.5: per-institution example course code for the search placeholder.
  const exampleCourseCode = useInstitutionTheme().exampleCourseCodes[0] ?? 'CMSC131';

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Search for a course</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
            placeholder={`e.g., ${exampleCourseCode} or Introduction to Programming`}
            autoFocus
          />
          {/* Centered with auto margins, not -translate-y-1/2: the `spin`
              keyframes replace the element transform, which would drop the
              centering for as long as it spins. */}
          {searchLoading && (
            <Loader2 className="absolute right-3 inset-y-0 my-auto h-4 w-4 text-gray-400 animate-spin" />
          )}
        </div>
      </div>

      {sortedResults.length > 0 && (
        <>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{sortedResults.length} results</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-opacity-50"
            >
              <option value="relevance">Sort: Relevance</option>
              <option value="code">Course Code</option>
              {showGradeUI && (
                <>
                  <option value="gpa_desc">Easiest First</option>
                  <option value="gpa_asc">Hardest First</option>
                </>
              )}
            </select>
          </div>
          <div className="border border-gray-200 rounded-md max-h-60 overflow-y-auto">
            {sortedResults.map((course) => {
              const inPlan = isCourseInPlan(course.code);
              const isBookmarked = context === 'bookmark' && isCourseBookmarked(course.code);

              return (
                <button
                  key={course.code || course.id}
                  onClick={() => setSelectedCourse(course)}
                  disabled={isBookmarked}
                  className={`w-full px-3 py-2 text-left border-b border-gray-100 last:border-b-0 transition-colors ${
                    isBookmarked
                      ? 'bg-gray-50 cursor-not-allowed opacity-60'
                      : selectedCourse?.code === course.code
                        ? selectedBgClass
                        : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{course.code}</span>
                        <span className="text-sm text-gray-500">{course.credits} cr</span>
                        {inPlan && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                            In Plan
                          </span>
                        )}
                        {isBookmarked && (
                          <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-700 rounded">
                            Bookmarked
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate">{course.title}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {searchQuery.length >= 2 && !searchLoading && sortedResults.length === 0 && (
        <div className="text-center py-4 text-gray-500">
          <p className="text-sm">No courses found matching "{searchQuery}"</p>
          <button
            onClick={() => setMode('placeholder')}
            className={`mt-2 text-sm text-${accentColor}-600 hover:text-${accentColor}-700 hover:underline`}
          >
            Create a placeholder instead
          </button>
        </div>
      )}

      {selectedCourse && (
        <div className={`${selectedBgClass} border border-gray-200 rounded-md p-3`}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-gray-900">{selectedCourse.code}</span>
            <span className="text-sm text-gray-700">{selectedCourse.credits} credits</span>
          </div>
          <p className="text-sm text-gray-800">{selectedCourse.title}</p>

          {showGradeUI && selectedCourse.grade_data?.distribution && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <GradeDistributionChart
                distribution={selectedCourse.grade_data.distribution}
                totalStudents={selectedCourse.grade_data.total_students}
                averageGpa={selectedCourse.grade_data.average_gpa}
              />
            </div>
          )}
        </div>
      )}

      {context === 'other_credits' && selectedCourse && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Grade Earned (optional)
          </label>
          <select
            value={placeholderData.grade ?? ''}
            onChange={(e) => handleInputChange('grade', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
          >
            {gradeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {errors.course && <p className="text-sm text-red-600">{errors.course}</p>}
    </div>
  );
};

export default SearchPanel;
